import { ref, Ref, onUnmounted } from 'vue'
import api from '@/api'
import util from '@/helpers/util'

export function useWhatsAppEngine(loadAccounts?: () => Promise<void>) {
    const engineRunning = ref(false)
    const isStarting = ref(false)
    const isStopping = ref(false)

    let statusInterval: number | null = null

    const checkEngineStatus = async () => {
        try {
            const response = await api.whatsapp.getEngineStatus()
            const wasRunning = engineRunning.value
            engineRunning.value = response.data.running

            return {
                wasRunning,
                running: engineRunning.value,
                queueProcessingEnabled: response.data.queueProcessingEnabled || false
            }
        } catch (error) {
            console.error('Failed to check engine status:', error)
            return null
        }
    }

    const startEngine = async () => {
        isStarting.value = true
        try {
            if (loadAccounts) {
                await loadAccounts()
            }

            await api.whatsapp.startEngine()
            engineRunning.value = true

            return true
        } catch (error) {
            console.error('Failed to start engine:', error)
            util.showToast('Failed to start engine. Please try again.', { icon: 'error', iconClass: 'toast-icon-circle' })
            return false
        } finally {
            isStarting.value = false
        }
    }

    const stopEngine = async () => {
        isStopping.value = true
        try {
            await api.whatsapp.stopEngine()
            engineRunning.value = false

            if (statusInterval) {
                clearInterval(statusInterval)
                statusInterval = null
            }

            return true
        } catch (error) {
            console.error('Failed to stop engine:', error)
            util.showToast('Failed to stop engine. Please try again.', { icon: 'error', iconClass: 'toast-icon-circle' })
            return false
        } finally {
            isStopping.value = false
        }
    }

    const toggleEngine = async () => {
        if (engineRunning.value) {
            await stopEngine()
        } else {
            await startEngine()
        }
    }

    const startStatusPolling = (
        onPoll: () => void,
        stopCondition?: () => boolean
    ) => {
        if (statusInterval) {
            clearInterval(statusInterval)
        }
        statusInterval = window.setInterval(() => {
            if (stopCondition && stopCondition()) {
                if (statusInterval) {
                    clearInterval(statusInterval)
                    statusInterval = null
                }
                return
            }
            onPoll()
        }, 5000)
    }

    const stopStatusPolling = () => {
        if (statusInterval) {
            clearInterval(statusInterval)
            statusInterval = null
        }
    }

    onUnmounted(() => {
        stopStatusPolling()
    })

    const updateEngineStatusFromData = (running: boolean, queueProcessingEnabled?: boolean) => {
        engineRunning.value = running
        // Note: queueProcessingEnabled is handled by queue composable
    }

    return {
        engineRunning,
        isStarting,
        isStopping,
        startEngine,
        stopEngine,
        toggleEngine,
        checkEngineStatus,
        startStatusPolling,
        stopStatusPolling,
        updateEngineStatusFromData
    }
}

